// ─────────────────────────────────────────────────────────────────────────────
// DERIVATION — merges the persisted per-step state with the static catalog and
// the account's module entitlements to produce the fully-computed guide the UI
// renders. Nothing here is persisted; this is the client's half of the contract.
//
// Rules (mirroring the backend contract doc):
//  - resolved        → completed_at != null || skipped_at != null
//  - step COMPLETE   → completed_at != null
//  - step SKIPPED    → only skipped_at != null
//  - step LOCKED     → level locked by module, OR prerequisite not completed
//  - step AVAILABLE  → otherwise
//  - level locked    → requiresModule && module inactive
//  - guide hidden    → every applicable step (in unlocked levels) is resolved
//  - guide completed → every `countsForCompletion` step has completed_at
//  - guide dismissed → hidden && not completed (resolved via skips)
//
// IN_PROGRESS (e.g. "planograma em edição") would require live domain signals we
// don't have here, so it is intentionally not derived yet.
// ─────────────────────────────────────────────────────────────────────────────
import type { SystemModulesDto } from '@/app/models/SystemModulesDto';
import type { SetupStepStateDto } from '../../core/_models';
import { SETUP_GUIDE_CATALOG, MODULES_UPSELL_ROUTE, type StepActionType, type StepCatalogEntry } from './catalog';

export type DerivedStepState = 'LOCKED' | 'AVAILABLE' | 'COMPLETE' | 'SKIPPED';

export type DerivedStep = {
  code: string;
  title: string;
  description: string;
  actionType: StepActionType;
  skippable: boolean;
  state: DerivedStepState;
  detail?: string;
  route?: string;
  icon: string;
  showEnvPanel?: boolean;
};

export type DerivedGroup = { title?: string; steps: DerivedStep[] };

export type DerivedLevel = {
  code: string;
  title: string;
  description: string;
  optional: boolean;
  locked: boolean;
  lockReason?: string;
  upsellRoute?: string;
  progressPercentage: number;
  /** Resolved steps (completed OR skipped). */
  completedSteps: number;
  totalSteps: number;
  groups: DerivedGroup[];
};

export type DerivedGuide = {
  levels: DerivedLevel[];
  hidden: boolean;
  completed: boolean;
  dismissed: boolean;
};

type StepState = Pick<SetupStepStateDto, 'completed_at' | 'skipped_at'>;

const isCompleted = (s?: StepState): boolean => !!s?.completed_at;
const isResolved = (s?: StepState): boolean => !!s?.completed_at || !!s?.skipped_at;

// `name` is the module's stable code (it's what SubscriptionDetailsTab uses as the
// React key, while `description`/`extra` are the human-facing title/subtitle).
export const isModuleActive = (modules: SystemModulesDto | undefined, code: string): boolean =>
  !!modules?.modules.some((m) => m.active && m.name === code);

const titleByCode = new Map<string, string>(
  SETUP_GUIDE_CATALOG.flatMap((level) =>
    level.groups.flatMap((group) => group.steps.map((s): [string, string] => [s.code, s.title])),
  ),
);

const deriveStep = (step: StepCatalogEntry, stateByCode: Map<string, StepState>, levelLocked: boolean): DerivedStep => {
  const own = stateByCode.get(step.code);
  const base = {
    code: step.code,
    title: step.title,
    description: step.description,
    actionType: step.actionType,
    skippable: step.skippable,
    route: step.route,
    icon: step.icon,
    showEnvPanel: step.showEnvPanel,
  };

  if (isCompleted(own)) return { ...base, state: 'COMPLETE' };
  if (isResolved(own)) return { ...base, state: 'SKIPPED' };
  if (levelLocked) return { ...base, state: 'LOCKED' };

  if (step.dependsOn && !isCompleted(stateByCode.get(step.dependsOn))) {
    const depTitle = titleByCode.get(step.dependsOn);
    return {
      ...base,
      state: 'LOCKED',
      detail: depTitle ? `Conclua "${depTitle}" primeiro.` : undefined,
    };
  }

  return { ...base, state: 'AVAILABLE' };
};

export const deriveGuide = (state: SetupStepStateDto[], modules: SystemModulesDto | undefined): DerivedGuide => {
  const stateByCode = new Map<string, StepState>(
    state.map((s): [string, StepState] => [s.code, { completed_at: s.completed_at, skipped_at: s.skipped_at }]),
  );

  const levels: DerivedLevel[] = SETUP_GUIDE_CATALOG.map((level) => {
    const locked = !!level.requiresModule && !isModuleActive(modules, level.requiresModule);

    const groups: DerivedGroup[] = level.groups.map((group) => ({
      title: group.title,
      steps: group.steps.map((step) => deriveStep(step, stateByCode, locked)),
    }));

    const allSteps = groups.flatMap((g) => g.steps);
    const totalSteps = allSteps.length;
    const completedSteps = allSteps.filter((s) => s.state === 'COMPLETE' || s.state === 'SKIPPED').length;
    const progressPercentage = totalSteps === 0 ? 0 : Math.round((completedSteps / totalSteps) * 100);

    return {
      code: level.code,
      title: level.title,
      description: level.description,
      optional: level.optional,
      locked,
      lockReason: locked ? level.lockReason : undefined,
      upsellRoute: locked ? MODULES_UPSELL_ROUTE : undefined,
      progressPercentage,
      completedSteps,
      totalSteps,
      groups,
    };
  });

  // Applicable = steps in levels that are not module-locked.
  const applicableSteps = levels.filter((l) => !l.locked).flatMap((l) => l.groups.flatMap((g) => g.steps));
  const hidden =
    applicableSteps.length > 0 && applicableSteps.every((s) => s.state === 'COMPLETE' || s.state === 'SKIPPED');

  const completed = SETUP_GUIDE_CATALOG.every((level) =>
    level.groups.every((group) =>
      group.steps.every((step) => !step.countsForCompletion || isCompleted(stateByCode.get(step.code))),
    ),
  );

  return { levels, hidden, completed, dismissed: hidden && !completed };
};
